import { supabase } from './supabase';
import { getCurrentUserOrganizationId } from './stores/referenceDataCache';
import type { User } from '@supabase/supabase-js';

/**
 * AuthManager - Singleton per gestire le chiamate di autenticazione
 * Deduplica le chiamate simultanee a auth.getUser() e organizationId
 */
class AuthManager {
  private static instance: AuthManager;
  private userPromise: Promise<User | null> | null = null;
  private orgIdPromise: Promise<string> | null = null;
  private lastFetchTime: number = 0;
  private readonly CACHE_DURATION = 5000; // 5 secondi di cache

  private constructor() {}

  static getInstance(): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager();
    }
    return AuthManager.instance;
  }

  /**
   * Ottiene l'utente corrente con deduplication
   * Le chiamate simultanee condivideranno la stessa Promise
   */
  async getUser(): Promise<User | null> {
    const now = Date.now();

    // Se c'è già una richiesta in corso, riutilizza quella Promise
    if (this.userPromise && (now - this.lastFetchTime) < this.CACHE_DURATION) {
      return this.userPromise;
    }

    // Crea una nuova Promise e memorizzala
    this.lastFetchTime = now;
    this.userPromise = supabase.auth.getUser()
      .then(response => {
        if (response.error) {
          console.error('Auth error:', response.error);
          return null;
        }
        return response.data.user;
      })
      .catch(error => {
        console.error('Failed to get user:', error);
        return null;
      })
      .finally(() => {
        // Dopo CACHE_DURATION, reset della promise per permettere nuove fetch
        setTimeout(() => {
          if (this.userPromise && (Date.now() - this.lastFetchTime) >= this.CACHE_DURATION) {
            this.userPromise = null;
          }
        }, this.CACHE_DURATION);
      });

    return this.userPromise;
  }

  /**
   * Ottiene l'organization ID con deduplication
   * Le chiamate simultanee condivideranno la stessa Promise
   */
  async getOrganizationId(): Promise<string> {
    const now = Date.now();

    // Se c'è già una richiesta in corso, riutilizza quella Promise
    if (this.orgIdPromise && (now - this.lastFetchTime) < this.CACHE_DURATION) {
      return this.orgIdPromise;
    }

    // Crea una nuova Promise e memorizzala
    this.orgIdPromise = getCurrentUserOrganizationId()
      .catch(error => {
        console.error('Failed to get organization ID:', error);
        throw error;
      })
      .finally(() => {
        // Dopo CACHE_DURATION, reset della promise per permettere nuove fetch
        setTimeout(() => {
          if (this.orgIdPromise && (Date.now() - this.lastFetchTime) >= this.CACHE_DURATION) {
            this.orgIdPromise = null;
          }
        }, this.CACHE_DURATION);
      });

    return this.orgIdPromise;
  }

  /**
   * Pulisce la cache - da chiamare al logout o cambio utente
   */
  clear(): void {
    this.userPromise = null;
    this.orgIdPromise = null;
    this.lastFetchTime = 0;
  }

  /**
   * Invalida la cache forzando un nuovo fetch alla prossima chiamata
   */
  invalidate(): void {
    this.lastFetchTime = 0;
  }
}

// Export singleton instance
export const authManager = AuthManager.getInstance();
